// Minimize / restore a game on the browser-local minimized list (#654).
//
// Sibling of SaveGameToggle, and deliberately built to the same contract: the
// click-isolation behavior below IS an acceptance criterion, and it is exactly
// what regresses silently when someone edits the row markup later.
//
// Placement note: renders inside the Date cell beside the bookmark, for the
// reason SaveGameToggle documents — every row type spells out its own cells, so
// a new column would have to be threaded through all of them plus the spacer's
// colSpan arithmetic.
//
// Misclick note (#651): this puts a SECOND small target in one cell, so an
// accidental minimize while reaching for the bookmark takes a game off the
// board. Two mitigations, and neither may be dropped. (1) This button matches
// SaveGameToggle's geometry EXACTLY and the pair is spaced by the wrapper in
// GameTable, not by shrinking either target — so a row without this control
// stays pixel-identical to today, which is what keeps non-CFB boards unchanged.
// (2) The act is cheap to undo, and NOT only in bulk: the board footer names
// every matchup it hid behind a disclosure, each with its own Restore, so a
// misclick costs one click to reverse rather than discarding every other mark.
// "Show all" sits beside it for the start-over case. This row's own control
// cannot be the undo — a misclick removes the row it lives on.
//
// Glyph note: EyeOff, not a chevron or an X. A chevron reads as "collapse this
// row in place" (it stays), and an X reads as "delete" (it's gone for good).
// Neither is true — the game is hidden from this board and restorable — and
// "hidden" is what an eye with a slash says.

import { EyeOff } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useMinimizedGames, isRowMinimized } from '@/stores/minimizedGames';
import { gameMarkId } from '@/stores/gameMarkIdentity';
import { useAuthStore } from '@/stores/authStore';
import type { GameRow } from './types';

export function MinimizeGameToggle({ game }: { game: GameRow }) {
  const markable = gameMarkId(game.key) != null;
  // Subscribed, not read once: the control must re-render the moment the
  // signed-in user changes, so a list belonging to the previous account cannot
  // survive an account switch on screen even for a frame.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const minimized = useMinimizedGames((s) => isRowMinimized(game.key, s, viewerId));
  const toggle = useMinimizedGames((s) => s.toggle);

  // Playoff-series rows carry a fabricated "9999-<round>-<yy>" date and so have
  // no canonical game key — nothing to minimize.
  if (!markable) return null;

  const matchup = `${game.awayCode} @ ${game.homeCode}`;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Both are load-bearing, for the reasons SaveGameToggle spells out: the
        // whole <tr> carries an onClick that expands the game's ladders, and on
        // most sports this button sits beside a <Link> to game detail inside
        // that row. A minimize click must trigger neither.
        e.stopPropagation();
        e.preventDefault();
        toggle(game, viewerId);
      }}
      aria-pressed={minimized}
      aria-label={
        minimized ? `Restore ${matchup} to the board` : `Minimize ${matchup}`
      }
      title={minimized ? 'Restore this game to the board' : 'Minimize this game'}
      className={cn(
        'shrink-0 rounded p-0.5 transition-colors',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
        minimized ? 'text-foreground' : 'text-muted-foreground/40 hover:text-foreground',
      )}
    >
      <EyeOff size={12} />
    </button>
  );
}
